import { useEffect, useMemo } from 'react';

import { useUser } from '@clerk/react';
import { DoBoxExecutorFeature, ExecutorMode } from '@this+that/models';
import log from 'loglevel';

import { useMyDoBoxExecutorFeatures } from '../../services/doBoxExecutor.service';

export interface DoBoxExecutorFlags {
  executor: boolean;
  plan: boolean;
  capabilityGaps: boolean;
  codeDelegation: boolean;
  memory: boolean;
  fanOut: boolean;
  executeDelegation: boolean;
  routes: boolean;
  standingPermissions: boolean;
  agentSetup: boolean;
  peopleMessaging: boolean;
  concurrentAgentRuns: boolean;
}

/** Fail-closed default — every flag off. Returned on the first load, on error,
 *  and for a signed-out caller. A refetch keeps the last resolved value. */
export const DEFAULT_DOBOX_EXECUTOR_FLAGS: DoBoxExecutorFlags = {
  executor: false,
  plan: false,
  capabilityGaps: false,
  codeDelegation: false,
  memory: false,
  fanOut: false,
  executeDelegation: false,
  routes: false,
  standingPermissions: false,
  agentSetup: false,
  peopleMessaging: false,
  concurrentAgentRuns: false,
};

export interface UseDoBoxExecutorFlagsResult {
  flags: DoBoxExecutorFlags;
  isLoaded: boolean;
  refetch: () => void;
}

function mapAccessToFlags(
  mode: ExecutorMode,
  features: DoBoxExecutorFeature[],
): DoBoxExecutorFlags {
  const featureSet = new Set(features);
  return {
    executor: mode === ExecutorMode.ON,
    plan: featureSet.has(DoBoxExecutorFeature.PLAN_V4),
    capabilityGaps: featureSet.has(DoBoxExecutorFeature.CAPABILITY_GAPS),
    codeDelegation: featureSet.has(DoBoxExecutorFeature.CODE_DELEGATION),
    memory:
      featureSet.has(DoBoxExecutorFeature.MEMORY_CAPTURE) ||
      featureSet.has(DoBoxExecutorFeature.MEMORY_RECALL),
    fanOut: featureSet.has(DoBoxExecutorFeature.MEMORY_FANOUT),
    executeDelegation: featureSet.has(DoBoxExecutorFeature.EXECUTE_TASK_TYPE),
    routes: featureSet.has(DoBoxExecutorFeature.AGENT_ROUTES),
    standingPermissions: featureSet.has(
      DoBoxExecutorFeature.STANDING_PERMISSIONS,
    ),
    agentSetup: featureSet.has(DoBoxExecutorFeature.AGENT_SETUP),
    peopleMessaging:
      featureSet.has(DoBoxExecutorFeature.PEOPLE_MESSAGING) &&
      featureSet.has(DoBoxExecutorFeature.PEOPLE_DIRECTORY),
    concurrentAgentRuns: featureSet.has(
      DoBoxExecutorFeature.CONCURRENT_AGENT_RUNS,
    ),
  };
}

/**
 * Resolves the caller's DoBox executor runtime flags. Fails closed: while
 * on the first load, on a query error, or for a signed-out caller, every flag
 * is false; a background refetch keeps the last resolved value. Never throws and never
 * logs the resolved access itself — only that the resolution failed.
 */
export function useDoBoxExecutorFlags(): UseDoBoxExecutorFlagsResult {
  const { isLoaded: isAuthLoaded, isSignedIn } = useUser();
  const { access, loading, error, refetch } = useMyDoBoxExecutorFeatures({
    skip: !isAuthLoaded || !isSignedIn,
  });

  useEffect(() => {
    if (error) {
      log.error(
        '[useDoBoxExecutorFlags] myDoBoxExecutorFeatures query failed',
        error,
      );
    }
  }, [error]);

  const flags = useMemo(() => {
    if (error || !access) {
      return DEFAULT_DOBOX_EXECUTOR_FLAGS;
    }
    return mapAccessToFlags(access.mode, access.features);
  }, [access, error]);

  return {
    flags,
    isLoaded: isAuthLoaded && (!loading || access != null),
    refetch,
  };
}
